import { useCallback, useEffect, useState } from 'react';

import {
  hasSelectionInUrl,
  PURCHASE_WIDGET_DATA_CHANGE_EVENT,
  PurchaseWidgetHTMLElement,
} from '~helpers';
import { PurchaseFlow } from '~organisms';
import { useHostElement } from '~providers';

/**
 * PurchaseWidget — React component rendered inside the purchase-widget custom element.
 *
 * Bridges the CE imperative API (.show(), .close(), .toggle(), .openWith(), `open` attribute)
 * to React state via the WidgetController. Each instance reads its own host from context, so
 * multiple <purchase-widget> elements on the same page stay isolated.
 *
 * `flowKey` bumps on close-end and on data-attribute mutation, so PurchaseFlow remounts with
 * fresh props on the next opening.
 */
export const PurchaseWidget = () => {
  const host = useHostElement<PurchaseWidgetHTMLElement>();

  const [isOpen, setIsOpen] = useState(() => host.hasAttribute('open') || hasSelectionInUrl());
  const [flowKey, setFlowKey] = useState(0);

  useEffect(() => {
    host._controller.setOpen = setIsOpen;

    const bumpFlowKey = () => setFlowKey((k) => k + 1);
    host.addEventListener(PURCHASE_WIDGET_DATA_CHANGE_EVENT, bumpFlowKey);
    return () => host.removeEventListener(PURCHASE_WIDGET_DATA_CHANGE_EVENT, bumpFlowKey);
  }, [host]);

  const handleExitTransitionEnd = useCallback(() => setFlowKey((k) => k + 1), []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    host.removeAttribute('open');
  }, [host]);

  return (
    <PurchaseFlow
      key={flowKey}
      isOpen={isOpen}
      onClose={handleClose}
      onExitTransitionEnd={handleExitTransitionEnd}
      initialCategoryShortname={host.dataset.productCategoryShortname ?? ''}
      initialValidityCategoryShortname={host.dataset.validityCategoryShortname}
      initialDate={host.dataset.date}
      initialParticipants={host.dataset.participants}
      initialContractorSlug={host.dataset.contractorSlug ?? undefined}
    />
  );
};
